import { type Nullable, VehicleModelType } from '@orcar/common';
import { INSURANCE_TYPE, simplifyPriceFormat } from '@orcar/common-d2c';

// 슈퍼카·쏠라티는 전 등급 만 30세/경력 3년, 그 외는 만 26세/경력 2년(일반면책만 1년).
const SOLATI_NAME_KEYWORDS = ['쏠라티', '솔라티'];

const isPremiumQualificationVehicle = (type?: string, name?: string): boolean =>
  type === VehicleModelType.SUPERCAR ||
  SOLATI_NAME_KEYWORDS.some((keyword) => (name ?? '').includes(keyword));

/**
 * 보험 등급별 대여 자격(최소 나이/운전경력) 고지 기준값.
 * - 슈퍼카·쏠라티: 만 30세 이상 / 운전경력 3년 이상 (전 등급)
 * - 그 외: 만 26세 이상 / 운전경력 2년 이상 (단, 일반면책은 1년 이상)
 */
export const getInsuranceQualification = (
  insuranceName?: string,
  vehicleType?: string,
  vehicleName?: string,
): { minAge: number; minExperience: number } => {
  if (isPremiumQualificationVehicle(vehicleType, vehicleName)) {
    return { minAge: 30, minExperience: 3 };
  }
  return {
    minAge: 26,
    minExperience: insuranceName === INSURANCE_TYPE.NORMAL ? 1 : 2,
  };
};

const normalRange = (title?: string, value?: Nullable<string>) => {
  switch (title) {
    case '면책금':
    case '자기부담금':
    case '자기부담금(면책금)':
      if (value) return value;
      return '50만원';

    case '수리기간 손해 보상금(휴차 보상료)':
    case '휴차 보상료':
      return '50% 고객 부담';

    case '보상 한도':
      if (value) return getInsuranceRangeWithValue(title, value);
      return '500만원';

    case '단독 사고':
      return '보상 제외';

    case '휠/타이어':
      return '보장불가';
  }
};

const perfectRange = (title?: string, value?: Nullable<string>) => {
  switch (title) {
    case '면책금':
    case '자기부담금':
    case '자기부담금(면책금)':
      return '100만원';

    case '휴차 보상료':
    case '수리기간 손해 보상금(휴차 보상료)':
      return '없음';

    case '보상 한도':
      if (value) return getInsuranceRangeWithValue(title, value);
      return '1,500만원';

    case '단독 사고':
      return '보상 포함\n(자기부담금 100만원)';

    case '휠/타이어':
      return '보장불가';
  }
};

const partialUnlimitedRange = (title?: string) => {
  switch (title) {
    case '자기부담금(면책금)':
    case '면책금':
    case '자기부담금':
      return '발생';

    case '보상 한도':
      return '무제한';

    case '수리기간 손해 보상금(휴차 보상료)':
    case '휴차 보상료':
      return '발생';

    case '단독 사고':
      return '보장불가';

    case '휠/타이어':
      return '보장';
  }
};

// 레거시 "고급면책 + 무제한특약" 보장내용 (완전면책 리브랜드 이전, e710399e7 기준)
const unlimitedRange = (title?: string, value?: Nullable<string>) => {
  switch (title) {
    case '면책금':
    case '자기부담금':
    case '자기부담금(면책금)':
      if (value) return value;
      return '면책금 발생';
    case '수리기간 손해 보상금(휴차 보상료)':
    case '휴차 보상료':
      return '면제';
    case '보상 한도':
      return '무제한';
    case '단독 사고':
      return '면책금 발생\n(대인/대물/자손 접수시 면책금 인당/건당 각각 발생)';
    case '휠/타이어':
      return '현장출동서비스 (휠, 타이어, 배터리, 차량잠금해제, 견인10KM이내) 1회성 소멸상품';
  }
};

// 완전면책 보장내용
const superRange = (title?: string) => {
  switch (title) {
    case '면책금':
    case '자기부담금':
    case '자기부담금(면책금)':
      return '없음';

    case '수리기간 손해 보상금(휴차 보상료)':
    case '휴차 보상료':
      return '없음';

    case '보상 한도':
      return '무제한';

    case '단독 사고':
      return '보상 가능';

    case '휠/타이어':
      return '보상 가능';
  }
};

const getInsuranceRangeWithValue = (title?: string, value?: string) => {
  switch (title) {
    case '면책금':
    case '자기부담금':
    case '휴차 보상료':
    case '자기부담금(면책금)':
    case '수리기간 손해 보상금(휴차 보상료)':
      return value;

    case '보상 한도':
      return simplifyPriceFormat(value);
  }
};

export const getCurrentInsuranceRange = (
  title?: string,
  insuranceName?: string,
  value?: Nullable<string>,
) => {
  switch (insuranceName) {
    case INSURANCE_TYPE.NORMAL:
      return normalRange(title, value);
    case INSURANCE_TYPE.PERFECT:
      return perfectRange(title, value);
    case INSURANCE_TYPE.SUPER:
      return superRange(title);
    case INSURANCE_TYPE.NONE:
      return '전액 부담';
    default:
      return '';
  }
};

export const getLockInsuranceRange = (
  title?: string,
  type?: string,
  value?: Nullable<string>,
) => {
  switch (type) {
    case INSURANCE_TYPE.NORMAL:
    case INSURANCE_TYPE.PARTIAL_UNLIMITED:
      return partialUnlimitedRange(title);
    case INSURANCE_TYPE.PERFECT:
    case INSURANCE_TYPE.UNLIMITED:
      return unlimitedRange(title, value);
    case INSURANCE_TYPE.SUPER:
      return superRange(title);
    case INSURANCE_TYPE.NONE:
      return perfectRange(title);
    default:
      return '';
  }
};

export const getPurchasedInsuranceDisplayType = (
  insuranceName?: string,
  isFullCoverage?: boolean,
): string | undefined => {
  if (!isFullCoverage) return insuranceName;

  switch (insuranceName) {
    case INSURANCE_TYPE.NORMAL:
      return INSURANCE_TYPE.PARTIAL_UNLIMITED;
    case INSURANCE_TYPE.PERFECT:
      return INSURANCE_TYPE.UNLIMITED;
    default:
      return insuranceName;
  }
};

export const getInsuranceName = (insuranceName?: string) => {
  switch (insuranceName) {
    case INSURANCE_TYPE.NORMAL:
      return '일반면책';
    case INSURANCE_TYPE.PERFECT:
      return '고급면책';
    case INSURANCE_TYPE.SUPER:
      return '완전면책';
    case INSURANCE_TYPE.UNLIMITED:
      return '고급면책 +\n무제한특약';
    case INSURANCE_TYPE.PARTIAL_UNLIMITED:
      return '일반면책 +\n무제한특약';
    case INSURANCE_TYPE.NONE:
      return '보험 미적용';
    case INSURANCE_TYPE.ALL:
    default:
      return '전체';
  }
};

/**
 * 보험 상품별 "보장 충실도"를 0~5 게이지로 환산한다.
 * 이름 아래 5칸 게이지로, 상품이 얼마나 폭넓게 보장하는지 한눈에 보여주기 위한 값이다.
 * (정확한 약관 수치가 아니라 상품 비교용 시각 지표)
 */
export const INSURANCE_GAUGE_MAX = 5;

export const getInsuranceGaugeLevel = (insuranceName?: string): number => {
  switch (insuranceName) {
    case INSURANCE_TYPE.SUPER:
      return 5;
    case INSURANCE_TYPE.UNLIMITED:
      return 4;
    case INSURANCE_TYPE.PERFECT:
    case INSURANCE_TYPE.PARTIAL_UNLIMITED:
      return 3;
    case INSURANCE_TYPE.NORMAL:
      return 2;
    case INSURANCE_TYPE.NONE:
      return 0;
    default:
      return 0;
  }
};

export const getTooltipInfo = (title: string, insuranceType?: string) => {
  switch (title) {
    case '휴차 보상료':
      return '사고 발생 후 차량 수리기간 동안의 영업 손실 비용을 뜻합니다.';
    case '단독 사고':
      return '사고의 상대방이 없는 자동차 사고로, 본인과실 100%이거나 주·정차된 차량을 접촉하는 사고입니다.';
    case '휠/타이어':
      // 휠/타이어 보장은 완전면책 전용 혜택이다.
      // 완전면책(또는 완전면책를 항상 포함하는 비교표: insuranceType 미지정)에서는 보장 내용을,
      // 고급/일반면책를 단독으로 표시할 때는 일반 안내 문구를 노출한다.
      if (
        insuranceType === undefined ||
        insuranceType === INSURANCE_TYPE.SUPER ||
        insuranceType === INSURANCE_TYPE.UNLIMITED
      ) {
        return '타이어 펑크 및 파손, 휠 파손, 견인(10km까지, 구난 제외), 방전에 대해 보상 (1회성 소멸)';
      }
      return '차량의 휠 또는 타이어가 파손된 경우로, 보험 보상한도 금액과 관계없이 별도로 수리비용이 발생합니다.';
    default:
      return;
  }
};
